import { useEffect } from "react";
import { useTranslation } from "react-i18next";

/**
 * Sets ``document.title`` to ``"<page> · PlateProfit"`` for the current
 * route and restores the brand name on unmount.
 *
 * ``value`` may be either:
 *   • a translation key prefixed ``pageTitles.`` → the hook translates it,
 *     so the browser tab reflects the chef's active locale.
 *   • a plain string → used verbatim (useful for dynamic cases like
 *     ``usePageTitle(menu?.restaurant_name)``).
 *   • ``null`` / ``undefined`` → leaves the title at the brand baseline.
 *
 * Example:
 *   usePageTitle("pageTitles.recipes");   // → "रेसिपी · PlateProfit" (hi)
 *   usePageTitle(menu.restaurant_name);   // → "Le Petit Bistro · PlateProfit"
 */
export default function usePageTitle(value) {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const brand = "PlateProfit";
    let page = null;
    if (typeof value === "string" && value.length > 0) {
      page = value.startsWith("pageTitles.")
        ? t(value, { defaultValue: null })
        : value;
    }
    document.title = page ? `${page} · ${brand}` : brand;

    return () => {
      document.title = brand;
    };
    // Re-run when the i18n language flips so an in-session language change
    // (e.g. the GlobalLanguageSwitcher) updates the browser-tab title too.
  }, [value, t, i18n.language]);
}
